import { downloadPDF } from '../../helpers/common';
import { title } from 'process';
import React, { useEffect, useState } from 'react';

export interface VisualAidSlide {
  slide_number: number;
  prompt?: string;
  title?:string;
  content?: string;
  imgUrl?: string;
  isGenerating?: boolean;
  error?: string | null;
  progress?: number; // 0 - 100
}

const MAX_DURATION = 80; // seconds (1 min 20 sec)

export function VisualAid({ slide }: { slide: VisualAidSlide }) {
  return (
    <div
      style={{
        width: "100%",
        aspectRatio: "16 / 9",
        background: "linear-gradient(135deg, #e8f4f8 0%, #d4e9f7 100%)",
        borderRadius: "12px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {slide.isGenerating && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.8)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "2rem",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 10 }}>
            Generating Image...
          </div>

          {/* Progress bar */}
          <div
            style={{
              width: "60%",
              height: "10px",
              background: "#e2e8f0",
              borderRadius: "6px",
              overflow: "hidden",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                width: `${slide.progress || 0}%`,
                height: "100%",
                background:
                  "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                transition: "width 0.5s linear",
              }}
            />
          </div>

          <div style={{ fontSize: "0.85rem", color: "#475569" }}>
            {Math.floor(slide.progress || 0)}%
          </div>
        </div>
      )}

      {slide.imgUrl && !slide.isGenerating ? (
        <img
          src={slide.imgUrl}
          alt="visual-aid"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : !slide.isGenerating ? (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          {slide.prompt || "No prompt provided"}
        </div>
      ) : null}
    </div>
  );
}

function SingleSlideView({
  slides,
  currentSlide,
  onSlideChange,
}: {
  slides: VisualAidSlide[];
  currentSlide: number;
  onSlideChange: (index: number) => void;
  onGenerateMessage?: (slide_number: number, prompt: string) => void;
  isGenerating?: boolean;
}) {
  const handlePrevious = () => {
    onSlideChange(Math.max(0, currentSlide - 1));
  };

  const handleNext = () => {
    onSlideChange(Math.min(slides.length - 1, currentSlide + 1));
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "center",
          alignItems: "center",
          padding: "1rem",
          backgroundColor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <button
          onClick={handlePrevious}
          disabled={currentSlide === 0}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "8px",
            border: "1px solid #cbd5e1",
            backgroundColor: "white",
            color: currentSlide === 0 ? "#94a3b8" : "#475569",
            cursor: currentSlide === 0 ? "not-allowed" : "pointer",
            fontSize: "0.875rem",
            fontWeight: "500",
            transition: "all 0.2s",
            boxShadow: currentSlide === 0 ? "none" : "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          ← Previous
        </button>
        
        <span
          style={{
            color: "#64748b",
            fontWeight: "600",
            fontSize: "0.875rem",
            minWidth: "120px",
            textAlign: "center",
          }}
        >
          Slide {currentSlide + 1} of {slides.length}
        </span>
        
        <button
          onClick={handleNext}
          disabled={currentSlide === slides.length - 1}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "8px",
            border: "1px solid #cbd5e1",
            backgroundColor: "white",
            color: currentSlide === slides.length - 1 ? "#94a3b8" : "#475569",
            cursor: currentSlide === slides.length - 1 ? "not-allowed" : "pointer",
            fontSize: "0.875rem",
            fontWeight: "500",
            transition: "all 0.2s",
            boxShadow: currentSlide === slides.length - 1 ? "none" : "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          Next →
        </button>
      </div>

      <div
        style={{
          flex: 1,
          padding: "2rem",
          overflow: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#ffffff",
        }}
      >
        <div 
          style={{ 
            width: "100%", 
            maxWidth: "1200px",
            aspectRatio: "16 / 9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <VisualAid
            slide={slides[currentSlide]}
          />
        </div>
      </div>
    </>
  );
}

function AllSlidesView({
  slides,
}: {
  slides: VisualAidSlide[];
}) {
  return (
    <div
      style={{
        flex: 1,
        padding: "2rem",
        overflow: "auto",
        backgroundColor: "#f8fafc",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
          gap: "2rem",
        }}
      >
        {slides.map((slide, index) => (
          <div
            key={index}
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 6px rgba(0,0,0,0.07)",
              transition: "all 0.3s",
              border: "1px solid #e2e8f0",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.07)";
            }}
          >
            <div
              style={{
                padding: "0.75rem 1rem",
                backgroundColor: "#f1f5f9",
                borderBottom: "1px solid #e2e8f0",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#475569",
              }}
            >
              Slide {slide.slide_number}
            </div>
            <div style={{ aspectRatio: "16 / 9" }}>
              <VisualAid
                slide={slide}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function PresentationViewer({
  slides,
  showAllSlides = false,
}: {
  slides: VisualAidSlide[];
  showAllSlides?: boolean;
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewMode, setViewMode] = useState<"single" | "all">(
    showAllSlides ? "all" : "single"
  );

  const containerStyle = {
    width: "100%",
    height: "100%",
    minHeight: "600px",
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    border: "1px solid #e2e8f0",
  };

  const headerStyle = {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "1.5rem 2rem",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    position: "relative" as const,
  };

  const titleStyle = {
    fontSize: "1.5rem",
    fontWeight: "700",
    color: "#ffffff",
    margin: 0,
    marginBottom: "0.25rem",
    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
  };

  const subtitleStyle = {
    fontSize: "0.95rem",
    color: "rgba(255,255,255,0.9)",
    margin: 0,
    fontWeight: "400",
  };

  const viewToggleStyle = {
    position: "absolute" as const,
    right: "2rem",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    gap: "0.5rem",
    backgroundColor: "rgba(255,255,255,0.15)",
    padding: "0.25rem",
    borderRadius: "10px",
    backdropFilter: "blur(10px)",
  };

  const toggleButtonStyle = (active: boolean) => ({
    padding: "0.5rem 1rem",
    borderRadius: "8px",
    border: "none",
    backgroundColor: active ? "white" : "transparent",
    color: active ? "#667eea" : "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "600",
    transition: "all 0.2s",
    boxShadow: active ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
  });

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}
      </style>

      <div style={headerStyle}>
        <h1 style={titleStyle}>Medical Presentation</h1>
        <p style={subtitleStyle}>Atorvastatin Clinical Overview</p>

        {!showAllSlides && (
          <div style={viewToggleStyle}>
            <button
              onClick={() => setViewMode("single")}
              style={toggleButtonStyle(viewMode === "single")}
            >
              Single View
            </button>
            <button
              onClick={() => setViewMode("all")}
              style={toggleButtonStyle(viewMode === "all")}
            >
              All Slides
            </button>
          </div>
        )}
      </div>

      {showAllSlides || viewMode === "all" ? (
        <AllSlidesView
          slides={slides}
        />
      ) : (
        <SingleSlideView
          slides={slides}
          currentSlide={currentSlide}
          onSlideChange={setCurrentSlide}
        />
      )}
    </div>
  );
}


function EditPromptsView({ slides, onSlidesChange, onGenerate }:{
  slides:any,
  onSlidesChange:(slides:any) => void,
  onGenerate:() => void
}) {
  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "1.5rem 2rem",
      }}>
        <h1 style={{ margin: 0, marginBottom: "0.25rem", fontSize: "1.5rem", fontWeight: "700", color: "#fff", textShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          Medical Presentation
        </h1>
        <p style={{ margin: 0, fontSize: "0.95rem", color: "rgba(255,255,255,0.9)", fontWeight: "400" }}>
          Atorvastatin Clinical Overview
        </p>
      </div>

      <div style={{ maxWidth: "780px", margin: "0 auto", padding: "2.5rem 1.5rem" }}>
        <p style={{ margin: "0 0 1.5rem", fontSize: "0.875rem", color: "#64748b" }}>
          Review and edit each slide prompt before generating.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {slides.map((slide:any, idx:number) => (
            <div
              key={slide.slide_number}
              style={{
                background: "white",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                padding: "1rem 1.25rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                display: "flex",
                gap: "1rem",
                alignItems: "flex-start",
              }}
            >
              <div style={{
                flexShrink: 0,
                width: "28px",
                height: "28px",
                borderRadius: "7px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: "700",
                marginTop: "2px",
              }}>
                {slide.slide_number}
              </div>

              <textarea
                value={slide.content}
                onChange={e => {
                  const updated = [...slides];
                  updated[idx] = { ...slide, content: e.target.value };
                  onSlidesChange(updated);
                }}
                rows={3}
                style={{
                  flex: 1,
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "0.6rem 0.875rem",
                  fontSize: "0.875rem",
                  lineHeight: "1.6",
                  color: "#374151",
                  fontFamily: "inherit",
                  resize: "vertical",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={e => e.target.style.borderColor = "#667eea"}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>
          ))}
        </div>

        <div style={{ marginTop: "2rem", display: "flex", justifyContent: "center" }}>
          <button
            onClick={onGenerate}
            style={{
              padding: "0.875rem 2.5rem",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              borderRadius: "10px",
              color: "white",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(102,126,234,0.35)",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(102,126,234,0.5)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(102,126,234,0.35)"; }}
          >
            ✨ Generate All Slides
          </button>
        </div>
      </div>
    </div>
  );
}

export function SlidesRenderer({
  slides,
  onGenerateMessage,
  isGenerating,
  showAllSlides = false,
  pdfMode = false,
  pdfLink = "",
  onAllGenerated,
}: {
  slides: VisualAidSlide[];
  onGenerateMessage?: (slide:any) => void;
  isGenerating?: boolean;
  showAllSlides?: boolean;
  pdfMode?: boolean;
  pdfLink?:string,
  onAllGenerated?:() => void

}) {
 const [phase, setPhase] = useState("edit");
  const [slides1,setSlides1] = useState(slides);
  const [loading,setLoading] = useState(false);

 

  useEffect(()=>{
    console.log("slides1==>",slides1)
  },[slides1])

  useEffect(()=>{
    console.log("slides==>",slides)
    setSlides1((prev:any)=>{
      const updated = [...prev].map((slide,i)=>{
        return {
          ...slide,
          imgUrl : slides[i].imgUrl,
          content : slides[i].content,
          slide_number : slides[i].slide_number,
          title:slides[i].title,
        }
      })
      return updated;
    })
  },[slides])

  const MAX_CONCURRENT = 4;

 const runGenerationQueue = async () => {
  const slidesCopy = [...slides1];
  let index = 0;
  setLoading(true)
  const runWorker = async () => {
    while (index < slidesCopy.length) {
      const currentIndex = index++;
      const slide = slidesCopy[currentIndex];

      let interval: any;
      let elapsed = 0;

      try {
        setSlides1(prev => {
          const updated = [...prev];
          updated[currentIndex] = {
            ...updated[currentIndex],
            isGenerating: true,
            progress: 0,
            error: null,
          };
          return updated;
        });

        interval = setInterval(() => {
          elapsed += 1;
          const percent = Math.min(95, (elapsed / MAX_DURATION) * 100);

          setSlides1(prev => {
            const updated = [...prev];
            if (!updated[currentIndex].isGenerating) return prev;
            updated[currentIndex] = {
              ...updated[currentIndex],
              progress: percent,
            };
            return updated;
          });

          if (elapsed >= MAX_DURATION) {
            clearInterval(interval);
          }
        }, 1000);

        await onGenerateMessage?.(slide);

        clearInterval(interval);

        setSlides1(prev => {
          const updated = [...prev];
          updated[currentIndex] = {
            ...updated[currentIndex],
            progress: 100,
            isGenerating: false,
          };
          return updated;
        });

      } catch (err: any) {
        clearInterval(interval);

        setSlides1(prev => {
          const updated = [...prev];
          updated[currentIndex] = {
            ...updated[currentIndex],
            isGenerating: false,
            error: "Failed to generate",
          };
          return updated;
        });
      }
    }
  };

  await Promise.all(
    Array.from({ length: MAX_CONCURRENT }).map(runWorker)
  );
  setLoading(false)
  // ✅ ALL DONE HERE
  onAllGenerated?.();
};
if (pdfMode) {
  return (
    <div
      style={{
        margin: 0,
        padding: 0,
      }}
    >
      {slides.map((slide, index) => (
        <div
          key={index}
          style={{
            width: "100%",
            height: "90vh",
            margin: 0,
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pageBreakAfter: "always"
          }}
        >
          {slide.imgUrl && (
            <img
              src={slide.imgUrl}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}
  if (phase === "edit") {
      return (<EditPromptsView 
        slides={slides1} 
        onSlidesChange={setSlides1} 
        onGenerate={()=>{
          setPhase("generate");
          runGenerationQueue();
        }} 
      />);
    }
  return (
    <div className="visual-aid-wrapper">
      {pdfLink && !loading &&  (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "1rem 1.5rem",
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0"
        }}
      >
        <p
          style={{
            padding: "0.6rem 1.5rem",
            borderRadius: "8px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontWeight: 600,
            fontSize: "0.9rem",
            textDecoration: "none",
            boxShadow: "0 4px 12px rgba(102,126,234,0.35)",
            transition: "all 0.2s",
            cursor: "pointer"
          }}
          onClick = {()=>{
            downloadPDF(pdfLink)
          }}
        >
          📄 Download PDF
        </p>
      </div>
    )}
      <PresentationViewer
        slides={slides1}
        showAllSlides={showAllSlides}
      />
    </div>
  );
}